import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    MobileStepper,
    Radio,
    RadioGroup,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@mui/material";
import { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import React from "react";
import CheckoutCart from "../../components/checkout/CheckoutCart";
import CartSummary from "../../components/checkout/CartSummary";
import PageWrapper from "../../components/PageWrapper";
import config from "../../config";
import {
    CANADA_ID,
    CountryOptions,
    ProvinceOptions,
    StateOptions,
    US_ID,
} from "../../models/AddressFormSelections";
import { ClientConfiguration } from "../../models/ClientConfiguration";
import { EcommerceEventList } from "../../models/Event";
import {
    AddressType,
    Order,
    OrderStatus,
    PaymentMethod,
    TransactionLogStatus,
} from "../../models/Order";
import {
    EcommercePayment,
    OfflinePayment,
    OrderAddress,
    OtherAddress,
    SquareOnlinePayment,
} from "../../requests/OrderRequests";
import AuthStore from "../../stores/AuthStore";
import { useCartStore } from "../../stores/CartStore";
import PaymentProcessingStore from "../../stores/PaymentProcessingStore";
import {
    getErrorMessage,
    priceFormatter,
    setupStyleMapFromStyleMap,
} from "../../utils/helpers";
import toast from "../../utils/toast";
import { useRouter } from "next/router";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { gtm } from "../../utils/gtm";
import SalesEndpoints from "../../api/SalesEndpoints";
import {
    getSavedCheckoutEmail,
    isValidEmail,
    saveCheckoutEmail,
} from "../../utils/checkoutEmailCookie";
import ApplicationStore from "../../stores/ApplicationStore";
import { getOrderMetadata } from "../../utils/orderMetadata";
import CartExpiryBanner from "../cart/CartExpiryBanner";
import InputField from "../form/InputField";
import CrossSellDialog from "../misc/CrossSellDialog";
import CartUpsellDialog from "./CartUpsellDialog";
import {
    FRONT_OF_THE_LINE_UPSELL,
    FRONT_ROW_VIP_UPSELL,
    WHISKY_CIGAR_UPSELL,
} from "../../config/cartUpsells";

const PaymentForm = dynamic(() => import("./PaymentForm"), { ssr: false });
const AddressForm = dynamic(() => import("./AddressForm"), { ssr: false });
const CustomFieldForm = dynamic(() => import("./CustomFieldForm"), {
    ssr: false,
});
const TicketCustomFieldsForm = dynamic(
    () => import("./TicketCustomFieldsForm"),
    {
        ssr: false,
    },
);

type Props = {
    eventList: EcommerceEventList;
    clientConfig: ClientConfiguration | null;
    baseUrl?: string;
};

const CheckoutForm = observer(({ eventList, clientConfig, baseUrl }: Props) => {
    const [order, setOrder] = React.useState<Order | null>(null);
    const orderRef = React.useRef<Order | null>(order);
    const timerRef = React.useRef<NodeJS.Timeout | null>(null);
    const [address, setAddress] = React.useState<OrderAddress>({
        firstName: "",
        lastName: "",
        address: "",
        address2: "",
        city: "",
        stateId: eventList?.clientId === 102 ? 2 : null,
        countryId: eventList?.clientId === 102 ? 1 : null,
        postal: "",
        phone: "",
        email: "",
        emailConsent: false,
    });
    const [shippingAddress, setShippingAddress] = React.useState<OtherAddress>({
        firstName: "",
        lastName: "",
        address: "",
        address2: "",
        city: "",
        stateId: null,
        countryId: null,
        phone: "",
        postal: "",
        type: AddressType.SHIPPING,
    });
    const [useShippingAddress, setUseShippingAddress] = React.useState(false);
    const [ecommercePayment, setEcommercePayment] =
        React.useState<EcommercePayment>({
            discriminator: "ECOMMERCE",
            cardNum: "",
            expiryMonth: "",
            expiryYear: "",
            nameOnCard: "",
            cvv: "",
        });

    const [offlinePayment, setOfflinePayment] = React.useState<OfflinePayment>({
        discriminator: "OFFLINE",
        paymentMethod: PaymentMethod.CASH,
    });
    const [ticketFields, setTicketFields] = React.useState<{
        [ticketId: number]: { [fieldId: number]: any };
    }>({});
    const [customFields, setCustomFields] = React.useState<
        {
            fieldId: number;
            value:
                | string
                | number
                | boolean
                | {
                      optionId: number;
                      value: string | null;
                  }
                | {
                      optionId: number;
                      value: string | null;
                  }[]
                | null;
        }[]
    >([]);
    const [donations, setDonations] = React.useState<{ [key: number]: number }>(
        {},
    );
    const [donationInputs, setDonationInputs] = React.useState<{
        [key: number]: string;
    }>({});
    const [isPaymentProcessing, setIsPaymentProcessing] = React.useState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);

    const [step, setStep] = React.useState(0);

    const [showConfirmDialog, setShowConfirmDialog] = React.useState(true);

    const [wantsToCancel, setWantsToCancel] = React.useState(false);
    const [showStandingRoomDialog, setShowStandingRoomDialog] =
        React.useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const addressRef = React.useRef<OrderAddress>(address);
    const ecommercePaymentRef =
        React.useRef<EcommercePayment>(ecommercePayment);
    const ticketFieldsRef = React.useRef<{
        [ticketId: number]: { [fieldId: number]: any };
    }>(ticketFields);

    addressRef.current = address;
    ecommercePaymentRef.current = ecommercePayment;
    ticketFieldsRef.current = ticketFields;

    const router = useRouter();

    function nextStep() {
        if (step === addressStep) {
            const cart = Object.assign({}, cartStore?.cart);
            gtm.trackBeginCheckout(cart);
            gtm.trackAddShippingInfo(cart);
        }
        setStep(step + 1);
    }

    function previousStep() {
        setStep(Math.max(0, step - 1));
        setShowConfirmDialog(true);
    }

    function goToEventList() {
        router.push(`${baseUrl ?? `/store/${eventList.cartUrl}`}`);
    }

    function sayThanks(o: Order) {
        gtm.trackPurchase(o);
        router.push(`/store/${o.client.cartUrl}/thank-you/${o.retrievalKey}`);
    }

    async function onSquareSubmit(nonce: string, verificationToken?: string) {
        if (!executeRecaptcha) return;
        const token = await executeRecaptcha("checkout");
        doSquareCheckout({
            discriminator: "SQUARE_ECOMMERCE",
            squareNonce: nonce,
            verificationToken,
            reCaptchaResponse: token,
        });
    }

    async function onStripeSubmit(): Promise<string> {
        if (!executeRecaptcha) return "";
        const token = await executeRecaptcha("checkout");

        if (isPaymentProcessing) return "";

        setIsPaymentProcessing(true);

        try {
            const o = await completeOrder(
                addressRef.current,
                {
                    ...ecommercePaymentRef.current,
                    reCaptchaResponse: token,
                },
                useShippingAddress ? shippingAddress : undefined,
            );
            setOrder(o);
            orderRef.current = o;
            return o.paymentIntent?.paymentIntentToken ?? "";
        } catch (err) {
            console.log(err);
            return "";
        }
    }

    function onStripeSuccess() {
        cartStore?.removePromoCode();
        cartStore?.removeSalesTrackingKey();
        cartStore?.nextCart();
        setOrder((o) => {
            if (o) {
                sayThanks(o);
            }
            return o;
        });
    }

    function onStripeError(err: string) {
        setIsPaymentProcessing(false);
        toast.error(err);
        setOrder((o) => {
            if (o) {
                try {
                    SalesEndpoints.cancelOrder({
                        retrievalKey: o.retrievalKey,
                        cartKey: cartStore?.cartKey?.key ?? "",
                    });
                } catch (_) {}
            }
            return null;
        });
    }

    async function onIframeSubmit() {
        if (!cartStore || !cartStore.cartKey || isPaymentProcessing) {
            if (!cartStore) {
                console.log("No cart store");
            }
            if (!cartStore?.cartKey) {
                console.log("No cart key");
            }
            if (isPaymentProcessing) {
                console.log("Payment processing");
            }
            return;
        }
        let token: string | undefined = undefined;
        if (executeRecaptcha) {
            token = await executeRecaptcha("checkout");
        }

        if (AuthStore.loggedIn && AuthStore.boxoffice) {
            completeOrder(addressRef.current, offlinePayment)
                .then((o) => {
                    if (o.status === OrderStatus.PAYMENT_PENDING) {
                        setOrder(o);
                        orderRef.current = o;
                    } else if (o.status === OrderStatus.PURCHASED) {
                        sayThanks(o);
                    }
                })
                .catch((err) => {
                    // Already caught by completeOrder
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        } else if (cartStore.total === 0) {
            completeOrder(
                addressRef.current,
                {
                    ...ecommercePaymentRef.current,
                    reCaptchaResponse: token,
                },
                useShippingAddress ? shippingAddress : undefined,
            )
                .then((o) => {
                    sayThanks(o);
                })
                .catch((err) => {
                    // Already caught by completeOrder
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        } else {
            PaymentProcessingStore.processPayment(
                cartStore.cartKey.key,
                addressRef.current,
                {
                    ...ecommercePaymentRef.current,
                    reCaptchaResponse: token,
                },
            )
                .then((decoratedEcommercePayment) => {
                    completeOrder(
                        addressRef.current,
                        decoratedEcommercePayment,
                        useShippingAddress ? shippingAddress : undefined,
                    )
                        .then((o) => {
                            sayThanks(o);
                        })
                        .catch((err) => {
                            // Already caught by completeOrder
                        })
                        .finally(() => {
                            setIsPaymentProcessing(false);
                        });
                })
                .catch((err) => {
                    let msg =
                        "We were unable to connect to our credit card processor. Please refresh the page and try again.";
                    if (err) {
                        msg = err.messages || err.message || err.text || err;
                    }
                    toast.error(msg);
                    // StoreRepository.remoteLogging.error(
                    //     "Accept or Cardinal frontend processing failed for client key " + this.props.eventStore.clientKey + ": " + err,
                    //     this.props.cartStore.cartKey.key
                    // );
                    PaymentProcessingStore.updateCart(
                        cartStore.cartKey!.key,
                        cartStore.promoCode,
                    );
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        }
    }

    const cartStore = useCartStore();
    const isLoggedIn = AuthStore.loggedIn;

    // Tracks the last (cartKey, email) pushed to the server so we don't re-send identical values.
    const lastSavedEmailRef = React.useRef<string>("");
    // The value we auto-filled from the cookie, so we can withdraw it if the user later logs in.
    const autofilledEmailRef = React.useRef<string>("");

    const saveEmailToCart = (email: string, consent: boolean) => {
        const key = cartStore?.cartKey?.key;
        if (!key || !isValidEmail(email)) return;
        const signature = `${key}|${email.trim()}`;
        if (lastSavedEmailRef.current === signature) return;
        lastSavedEmailRef.current = signature;
        SalesEndpoints.setCartEmail(key, email.trim(), consent).catch(() => {});
    };

    // On a return visit, prefill the email field from the cookie and tie it to the new cart
    // immediately, so a silent abandonment still has an email attached. Never populates for
    // logged-in (box office) users -- a shared browser entering many customers' emails -- and
    // withdraws a value we auto-filled if the user logs in after it was prefilled.
    React.useEffect(() => {
        if (isLoggedIn) {
            if (autofilledEmailRef.current) {
                setAddress((prev) =>
                    prev.email === autofilledEmailRef.current
                        ? { ...prev, email: "" }
                        : prev,
                );
                autofilledEmailRef.current = "";
            }
            return;
        }
        const key = cartStore?.cartKey?.key;
        const saved = getSavedCheckoutEmail();
        if (!key || !saved || !isValidEmail(saved)) return;
        setAddress((prev) => (prev.email ? prev : { ...prev, email: saved }));
        autofilledEmailRef.current = saved;
        saveEmailToCart(saved, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartStore?.cartKey?.key, isLoggedIn]);

    const onEmailBlur = () => {
        if (isLoggedIn || !isValidEmail(address.email)) return;
        saveCheckoutEmail(address.email);
        saveEmailToCart(address.email, address.emailConsent);
    };

    React.useEffect(() => {
        if (eventList?.clientId === 102 && cartStore?.cart?.items) {
            const hasStandingTickets = cartStore.cart.items.some((item) => {
                const sectionName =
                    item.seatmapSection?.name?.toLowerCase() ?? "";
                return (
                    sectionName.includes("standing") ||
                    sectionName.includes("saloon")
                );
            });
            if (hasStandingTickets) {
                setShowStandingRoomDialog(true);
            }
        }
    }, [cartStore?.cart?.items]);


    function doSquareCheckout(squarePayment: SquareOnlinePayment) {
        if (!squarePayment.squareNonce || isPaymentProcessing) return;

        setIsPaymentProcessing(true);

        completeOrder(
            addressRef.current,
            squarePayment,
            useShippingAddress ? shippingAddress : undefined,
        )
            .then((o) => {
                sayThanks(o);
            })
            .catch((err) => {
                // Already caught by completeOrder
            })
            .finally(() => {
                setIsPaymentProcessing(false);
            });
    }

    async function completeOrder(
        orderAddress: OrderAddress,
        payment: EcommercePayment | SquareOnlinePayment | OfflinePayment,
        shippingAddress?: OtherAddress,
    ): Promise<Order> {
        if (!cartStore) return Promise.reject("No cart store found");
        try {
            const o = await cartStore.completeOrder(
                orderAddress,
                payment,
                shippingAddress,
                getOrderMetadata(),
                ticketFieldsRef.current,
                customFields,
                donations,
            );

            if (o.status === OrderStatus.PAYMENT_PENDING) {
                return o;
            }

            const transactionLog = o.transactionLogs.reduce((a, b) =>
                new Date(a.transactionDate) > new Date(b.transactionDate)
                    ? a
                    : b,
            );
            if (transactionLog.status === TransactionLogStatus.SUCCESS) {
                cartStore.removePromoCode();
                cartStore.removeSalesTrackingKey();
                ApplicationStore.clearMetadata();
                cartStore.nextCart();
                return o;
            }
            if (transactionLog.status === TransactionLogStatus.DECLINED) {
                throw Error("Your payment was declined.");
            }
            throw Error(
                "An error occurred while processing transaction: " +
                    transactionLog.responseText,
            );
        } catch (err) {
            console.log(err);
            toast.error(getErrorMessage(err));
            throw err;
        }
    }

    function formIsValid(): boolean {
        // Check if terms need to be accepted
        if (eventList.termsAndConditions && !termsAccepted) {
            return false;
        }

        if (
            !AuthStore.boxoffice &&
            address.firstName &&
            address.lastName &&
            address.address &&
            address.city &&
            address.stateId &&
            address.countryId &&
            address.postal &&
            address.phone &&
            address.email
        ) {
            if (isNaN(address.stateId) || isNaN(address.countryId)) {
                return false;
            }
            return true;
        }
        if (AuthStore.boxoffice) {
            if (address.firstName && address.lastName && address.email) {
                return true;
            }
        }
        return false;
    }

    React.useEffect(() => {
        if (eventList.newStyleMap) {
            setupStyleMapFromStyleMap(eventList.newStyleMap);
        }
        if (clientConfig) {
            PaymentProcessingStore.setClientConfig(clientConfig);
        }
    }, []);

    async function checkOrderStatus() {
        if (orderRef.current) {
            try {
                const res = await SalesEndpoints.getOrderByRetrievalKey(
                    orderRef.current.retrievalKey,
                );
                if (res.status === OrderStatus.PURCHASED) {
                    toast.success("Order completed successfully.");
                    sayThanks(res);
                    cartStore?.nextCart();
                    orderRef.current = null;
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                    }
                } else if (res.status === OrderStatus.PAYMENT_FAILED) {
                    toast.error("Payment failed. Please try again.");
                    orderRef.current = null;
                    setOrder(null);
                    setWantsToCancel(false);
                } else {
                    setOrder(res);
                    orderRef.current = res;
                }
            } catch (err) {
                setOrder(null);
                orderRef.current = null;
                setWantsToCancel(false);
                toast.error("Payment failed. Please try again.");
            }
        }
    }

    React.useEffect(() => {
        timerRef.current = setInterval(() => {
            checkOrderStatus();
        }, 5_000);
    }, []);

    const ticketSubtotal = cartStore?.ticketSubtotal ?? 0;
    const subtotal = cartStore?.subtotal ?? 0;
    const authFee = cartStore?.authFee ?? 0;
    const cartTotal = cartStore?.total ?? 0;
    const donationsTotal = Object.values(donations).reduce((a, b) => a + b, 0);
    const total = cartTotal + donationsTotal;

    const showSubtotal = subtotal !== cartTotal;
    const hasItems = (cartStore?.quantity || 0) > 0;
    const clientDonations = cartStore?.cart?.donations ?? [];
    const numSteps = (() => {
        let steps = 2; // Address + Payment
        if (cartStore?.hasAnyCustomFields) steps++;
        if (clientDonations.length) steps++;
        return steps;
    })();
    const addressStep = 0;
    const customFieldStep = cartStore?.hasAnyCustomFields ? 1 : -1;
    const donationStep = clientDonations.length
        ? cartStore?.hasAnyCustomFields
            ? 2
            : 1
        : -1;
    const paymentStep = numSteps - 1;

    return (
        <PageWrapper eventList={eventList} baseUrl={baseUrl} noCart>
            <CrossSellDialog />
            <CartUpsellDialog
                cartStore={cartStore}
                upsellConfig={WHISKY_CIGAR_UPSELL}
            />
            <CartUpsellDialog
                cartStore={cartStore}
                upsellConfig={FRONT_ROW_VIP_UPSELL}
            />
            <CartUpsellDialog
                cartStore={cartStore}
                upsellConfig={FRONT_OF_THE_LINE_UPSELL}
            />
            <Container maxWidth="xl">
                <Box sx={{ my: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={goToEventList}
                    >
                        Back to Event List
                    </Button>
                </Box>

                {hasItems && cartStore?.shouldShowRefundMessage && (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="h5"
                            style={{ color: "red" }}
                            textAlign="center"
                        >
                            NO REFUND OR EXCHANGE ON TICKETS
                        </Typography>
                    </Box>
                )}

                {!hasItems ? (
                    // Empty Cart State
                    <Card sx={{ textAlign: "center", py: 6 }}>
                        <CardContent>
                            <Typography
                                variant="h4"
                                sx={{ mb: 2, color: "text.secondary" }}
                            >
                                Your cart is empty
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ mb: 4, color: "text.secondary" }}
                            >
                                It looks like you haven&apos;t added any tickets
                                yet, or your cart may have expired.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={goToEventList}
                                sx={{ minWidth: 200 }}
                            >
                                Browse Events
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={3}>
                        {/* Mobile Cart Summary - Only visible on small screens */}
                        <Grid
                            item
                            xs={12}
                            sx={{ display: { xs: "block", lg: "none" } }}
                        >
                            {cartStore && (
                                <CartSummary
                                    cartStore={cartStore}
                                    donations={donations}
                                />
                            )}
                        </Grid>

                        {/* Left Column - Checkout Steps */}
                        <Grid item xs={12} lg={8}>
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography
                                        variant="h4"
                                        sx={{ mb: 3, textAlign: "center" }}
                                    >
                                        Checkout
                                    </Typography>

                                    <Stepper
                                        activeStep={step}
                                        sx={{
                                            display: { xs: "none", md: "flex" },
                                            mb: 3,
                                        }}
                                    >
                                        <Step>
                                            <StepLabel>
                                                Address Information
                                            </StepLabel>
                                        </Step>
                                        {cartStore?.hasAnyCustomFields && (
                                            <Step>
                                                <StepLabel>
                                                    Ticket Information
                                                </StepLabel>
                                            </Step>
                                        )}
                                        {clientDonations.length && (
                                            <Step>
                                                <StepLabel>Donations</StepLabel>
                                            </Step>
                                        )}
                                        <Step>
                                            <StepLabel>Payment</StepLabel>
                                        </Step>
                                    </Stepper>

                                    <MobileStepper
                                        activeStep={step}
                                        variant="dots"
                                        steps={numSteps}
                                        position="static"
                                        backButton={<Box></Box>}
                                        nextButton={<Box></Box>}
                                        sx={{
                                            width: "100%",
                                            flexGrow: 1,
                                            display: { xs: "flex", md: "none" },
                                            mb: 3,
                                        }}
                                    />
                                    {/* Address Step */}
                                    {step === addressStep && (
                                        <Box sx={{ my: 2 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{ mb: 3 }}
                                            >
                                                Address Information
                                            </Typography>
                                            <form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    if (!formIsValid()) {
                                                        return toast.error(
                                                            "Please fill out all required fields.",
                                                        );
                                                    }
                                                    nextStep();
                                                }}
                                            >
                                                <AddressForm
                                                    termsAndConditions={
                                                        eventList.termsAndConditions
                                                    }
                                                    clientName={
                                                        eventList.clientName
                                                    }
                                                    clientId={
                                                        eventList.clientId
                                                    }
                                                    address={address}
                                                    setAddress={setAddress}
                                                    onEmailBlur={onEmailBlur}
                                                    termsAccepted={
                                                        termsAccepted
                                                    }
                                                    setTermsAccepted={
                                                        setTermsAccepted
                                                    }
                                                    deliveryMethodSlot={
                                                        <>
                                                            {eventList.deliveryMethods.filter(
                                                                (method) =>
                                                                    AuthStore.boxoffice ||
                                                                    !method.isBoxofficeOnly,
                                                            ).length > 1 && (
                                                            <FormControl
                                                                fullWidth
                                                                variant="outlined"
                                                                sx={{ mt: 3 }}
                                                            >
                                                                <InputLabel>
                                                                    Delivery
                                                                    Method
                                                                </InputLabel>
                                                                <Select
                                                                    fullWidth
                                                                    value={
                                                                        cartStore
                                                                            ?.deliveryMethod
                                                                            ?.id ??
                                                                        1
                                                                    }
                                                                    label="Delivery Method"
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        const methodId =
                                                                            Number(
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            );
                                                                        cartStore?.changeDeliveryMethod(
                                                                            methodId,
                                                                        );
                                                                    }}
                                                                >
                                                                    {eventList.deliveryMethods
                                                                        .filter(
                                                                            (
                                                                                method,
                                                                            ) =>
                                                                                AuthStore.boxoffice ||
                                                                                !method.isBoxofficeOnly,
                                                                        )
                                                                        .sort(
                                                                            (
                                                                                a,
                                                                                b,
                                                                            ) => {
                                                                                if (
                                                                                    a.id ===
                                                                                    1
                                                                                ) {
                                                                                    return -1;
                                                                                }
                                                                                return a.name.localeCompare(
                                                                                    b.name,
                                                                                );
                                                                            },
                                                                        )
                                                                        .map(
                                                                            (
                                                                                method,
                                                                            ) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        method.id
                                                                                    }
                                                                                    value={
                                                                                        method.id
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        method.name
                                                                                    }{" "}
                                                                                    {method.amount >
                                                                                        0 &&
                                                                                        `(${priceFormatter(
                                                                                            method.amount,
                                                                                        )})`}
                                                                                </MenuItem>
                                                                            ),
                                                                        )}
                                                                </Select>
                                                            </FormControl>
                                                            )}
                                                            {AuthStore.boxoffice &&
                                                                eventList.allowCustomAmount && (
                                                                <FormControl
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    sx={{ mt: 3 }}
                                                                >
                                                                    <TextField
                                                                        label="Custom Amount"
                                                                        type="number"
                                                                        defaultValue={
                                                                            (cartStore?.customAmount ??
                                                                                0) > 0
                                                                                ? (
                                                                                      (cartStore?.customAmount ??
                                                                                          0) /
                                                                                      100
                                                                                  ).toFixed(2)
                                                                                : ""
                                                                        }
                                                                        onBlur={(e) => {
                                                                            const dollars =
                                                                                parseFloat(
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                );
                                                                            const cents =
                                                                                isNaN(
                                                                                    dollars,
                                                                                )
                                                                                    ? 0
                                                                                    : Math.max(
                                                                                          0,
                                                                                          Math.round(
                                                                                              dollars *
                                                                                                  100,
                                                                                          ),
                                                                                      );
                                                                            if (
                                                                                cents !==
                                                                                (cartStore?.customAmount ??
                                                                                    0)
                                                                            ) {
                                                                                cartStore?.setCustomAmount(
                                                                                    cents,
                                                                                );
                                                                            }
                                                                        }}
                                                                        helperText="Taxable surcharge applied to this order"
                                                                        InputProps={{
                                                                            startAdornment:
                                                                                (
                                                                                    <InputAdornment position="start">
                                                                                        $
                                                                                    </InputAdornment>
                                                                                ),
                                                                        }}
                                                                        inputProps={{
                                                                            min: 0,
                                                                            step: "0.01",
                                                                        }}
                                                                    />
                                                                </FormControl>
                                                            )}
                                                        </>
                                                    }
                                                />
                                                {eventList.customFields.length >
                                                    0 && (
                                                    <Box sx={{ my: 3 }}>
                                                        <Typography
                                                            variant="h5"
                                                            sx={{ mb: 2 }}
                                                        >
                                                            Additional
                                                            Information
                                                        </Typography>
                                                        <CustomFieldForm
                                                            customFieldDefinitions={
                                                                eventList.customFields
                                                            }
                                                            customFields={
                                                                customFields
                                                            }
                                                            onChange={(
                                                                fieldId,
                                                                value,
                                                            ) => {
                                                                setCustomFields(
                                                                    (
                                                                        fields,
                                                                    ) => {
                                                                        const idx =
                                                                            fields.findIndex(
                                                                                (
                                                                                    f,
                                                                                ) =>
                                                                                    f.fieldId ===
                                                                                    fieldId,
                                                                            );
                                                                        if (
                                                                            idx ===
                                                                            -1
                                                                        ) {
                                                                            return [
                                                                                ...fields,
                                                                                {
                                                                                    fieldId,
                                                                                    value,
                                                                                },
                                                                            ];
                                                                        }
                                                                        return fields.map(
                                                                            (
                                                                                f,
                                                                                i,
                                                                            ) =>
                                                                                i ===
                                                                                idx
                                                                                    ? {
                                                                                          fieldId,
                                                                                          value,
                                                                                      }
                                                                                    : f,
                                                                        );
                                                                    },
                                                                );
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "flex-end",
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        type="submit"
                                                        disabled={
                                                            !cartStore?.hasItems
                                                        }
                                                        sx={{ minWidth: 120 }}
                                                    >
                                                        Next
                                                    </Button>
                                                </Box>
                                            </form>
                                        </Box>
                                    )}
                                    {/* Ticket Fields Step */}
                                    {step === customFieldStep &&
                                        cartStore?.itemsWithCustomFields && (
                                            <Box sx={{ my: 2 }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ mb: 3 }}
                                                >
                                                    Ticket Information
                                                </Typography>
                                                <TicketCustomFieldsForm
                                                    items={
                                                        cartStore.itemsWithCustomFields
                                                    }
                                                    ticketFields={ticketFields}
                                                    setTicketFields={
                                                        setTicketFields
                                                    }
                                                    onComplete={nextStep}
                                                    onBack={previousStep}
                                                />
                                            </Box>
                                        )}
                                    {/* Donations Step */}
                                    {step === donationStep && (
                                        <Box sx={{ my: 2 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{ mb: 3 }}
                                            >
                                                Donations
                                            </Typography>
                                            <form onSubmit={nextStep}>
                                                <Grid container spacing={2}>
                                                    {clientDonations.map(
                                                        (donation) => (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                key={
                                                                    donation.id
                                                                }
                                                            >
                                                                <Box
                                                                    sx={(
                                                                        theme,
                                                                    ) => ({
                                                                        p: 4,
                                                                        background:
                                                                            theme
                                                                                .palette
                                                                                .mode ===
                                                                            "light"
                                                                                ? `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[200]} 100%)`
                                                                                : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                                                        borderRadius: 2,
                                                                        boxShadow: 3,
                                                                    })}
                                                                >
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{
                                                                            mb: 2,
                                                                            fontWeight: 600,
                                                                            color: (
                                                                                theme,
                                                                            ) =>
                                                                                theme
                                                                                    .palette
                                                                                    .mode ===
                                                                                "light"
                                                                                    ? "text.primary"
                                                                                    : "white",
                                                                        }}
                                                                    >
                                                                        {
                                                                            donation.name
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            mb: 3,
                                                                            lineHeight: 1.6,
                                                                            color: (
                                                                                theme,
                                                                            ) =>
                                                                                theme
                                                                                    .palette
                                                                                    .mode ===
                                                                                "light"
                                                                                    ? "text.secondary"
                                                                                    : "rgba(255,255,255,0.9)",
                                                                        }}
                                                                    >
                                                                        {donation
                                                                            .config
                                                                            .description ??
                                                                            `Would you like to make a donation to ${donation.name}?`}
                                                                    </Typography>
                                                                    {donation
                                                                        .config
                                                                        .imageUrl && (
                                                                        <Box
                                                                            sx={{
                                                                                mb: 3,
                                                                                display:
                                                                                    "flex",
                                                                                justifyContent:
                                                                                    "center",
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={
                                                                                    donation
                                                                                        .config
                                                                                        .imageUrl
                                                                                }
                                                                                alt={
                                                                                    donation.name
                                                                                }
                                                                                style={{
                                                                                    maxWidth:
                                                                                        "100%",
                                                                                    width: "300px",
                                                                                    borderRadius:
                                                                                        "12px",
                                                                                    boxShadow:
                                                                                        "0 4px 12px rgba(0,0,0,0.15)",
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    )}

                                                                    {/* Show preset amounts if available */}
                                                                    {donation
                                                                        .config
                                                                        .amounts
                                                                        .length >
                                                                        0 && (
                                                                        <RadioGroup
                                                                            value={(() => {
                                                                                const currentValue =
                                                                                    donations[
                                                                                        donation
                                                                                            .id
                                                                                    ];
                                                                                if (
                                                                                    currentValue ===
                                                                                        undefined ||
                                                                                    currentValue ===
                                                                                        null
                                                                                ) {
                                                                                    return 0; // "No Thank You"
                                                                                }
                                                                                // If the value is in preset amounts, return it
                                                                                if (
                                                                                    donation.config.amounts.includes(
                                                                                        currentValue,
                                                                                    )
                                                                                ) {
                                                                                    return currentValue;
                                                                                }
                                                                                // Otherwise, it's a custom amount
                                                                                return -1;
                                                                            })()}
                                                                            onChange={(
                                                                                _e,
                                                                                value,
                                                                            ) => {
                                                                                const num =
                                                                                    parseInt(
                                                                                        value,
                                                                                        10,
                                                                                    );
                                                                                if (
                                                                                    num ===
                                                                                    0
                                                                                ) {
                                                                                    // "No Thank You" selected - remove donation
                                                                                    setDonations(
                                                                                        (
                                                                                            donations,
                                                                                        ) => {
                                                                                            const newDonations =
                                                                                                {
                                                                                                    ...donations,
                                                                                                };
                                                                                            delete newDonations[
                                                                                                donation
                                                                                                    .id
                                                                                            ];
                                                                                            return newDonations;
                                                                                        },
                                                                                    );
                                                                                    setDonationInputs(
                                                                                        (
                                                                                            inputs,
                                                                                        ) => {
                                                                                            const newInputs =
                                                                                                {
                                                                                                    ...inputs,
                                                                                                };
                                                                                            delete newInputs[
                                                                                                donation
                                                                                                    .id
                                                                                            ];
                                                                                            return newInputs;
                                                                                        },
                                                                                    );
                                                                                } else if (
                                                                                    num ===
                                                                                    -1
                                                                                ) {
                                                                                    // "Custom Amount" selected - reset input and set to -1
                                                                                    setDonations(
                                                                                        (
                                                                                            donations,
                                                                                        ) => ({
                                                                                            ...donations,
                                                                                            [donation.id]:
                                                                                                -1,
                                                                                        }),
                                                                                    );
                                                                                    // Always reset to "0.00" when clicking Custom Amount
                                                                                    setDonationInputs(
                                                                                        (
                                                                                            inputs,
                                                                                        ) => ({
                                                                                            ...inputs,
                                                                                            [donation.id]:
                                                                                                "0.00",
                                                                                        }),
                                                                                    );
                                                                                } else {
                                                                                    // Preset amount selected
                                                                                    setDonations(
                                                                                        (
                                                                                            donations,
                                                                                        ) => ({
                                                                                            ...donations,
                                                                                            [donation.id]:
                                                                                                num,
                                                                                        }),
                                                                                    );
                                                                                    // Clear custom input
                                                                                    setDonationInputs(
                                                                                        (
                                                                                            inputs,
                                                                                        ) => {
                                                                                            const newInputs =
                                                                                                {
                                                                                                    ...inputs,
                                                                                                };
                                                                                            delete newInputs[
                                                                                                donation
                                                                                                    .id
                                                                                            ];
                                                                                            return newInputs;
                                                                                        },
                                                                                    );
                                                                                }
                                                                            }}
                                                                            sx={{
                                                                                mb: donation.allowCustomAmount
                                                                                    ? 2
                                                                                    : 0,
                                                                            }}
                                                                        >
                                                                            <FormControlLabel
                                                                                value={
                                                                                    0
                                                                                }
                                                                                control={
                                                                                    <Radio />
                                                                                }
                                                                                label="No Thank You"
                                                                            />
                                                                            {donation.config.amounts.map(
                                                                                (
                                                                                    amount,
                                                                                ) => (
                                                                                    <FormControlLabel
                                                                                        key={
                                                                                            amount
                                                                                        }
                                                                                        value={
                                                                                            amount
                                                                                        }
                                                                                        control={
                                                                                            <Radio />
                                                                                        }
                                                                                        label={priceFormatter(
                                                                                            amount,
                                                                                        )}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                            {donation.allowCustomAmount && (
                                                                                <FormControlLabel
                                                                                    value={
                                                                                        -1
                                                                                    }
                                                                                    control={
                                                                                        <Radio />
                                                                                    }
                                                                                    label="Custom Amount"
                                                                                />
                                                                            )}
                                                                        </RadioGroup>
                                                                    )}

                                                                    {/* Show custom amount input if allowed and either no preset amounts or "Custom Amount" is selected */}
                                                                    {donation.allowCustomAmount &&
                                                                        (donation
                                                                            .config
                                                                            .amounts
                                                                            .length ===
                                                                            0 ||
                                                                            (donations[
                                                                                donation
                                                                                    .id
                                                                            ] &&
                                                                                !donation.config.amounts.includes(
                                                                                    donations[
                                                                                        donation
                                                                                            .id
                                                                                    ],
                                                                                ))) && (
                                                                            <Box
                                                                                sx={{
                                                                                    maxWidth: 300,
                                                                                    mt:
                                                                                        donation
                                                                                            .config
                                                                                            .amounts
                                                                                            .length >
                                                                                        0
                                                                                            ? 2
                                                                                            : 0,
                                                                                }}
                                                                            >
                                                                                <TextField
                                                                                    fullWidth
                                                                                    type="number"
                                                                                    label="Custom Donation Amount"
                                                                                    value={
                                                                                        donationInputs[
                                                                                            donation
                                                                                                .id
                                                                                        ] ??
                                                                                        "0.00"
                                                                                    }
                                                                                    sx={{
                                                                                        "& .MuiOutlinedInput-root":
                                                                                            {
                                                                                                backgroundColor:
                                                                                                    "white",
                                                                                                "&:hover":
                                                                                                    {
                                                                                                        backgroundColor:
                                                                                                            "white",
                                                                                                    },
                                                                                            },
                                                                                        "& .MuiInputLabel-root":
                                                                                            {
                                                                                                backgroundColor:
                                                                                                    "transparent",
                                                                                            },
                                                                                    }}
                                                                                    onChange={(
                                                                                        e,
                                                                                    ) => {
                                                                                        const value =
                                                                                            e
                                                                                                .target
                                                                                                .value;
                                                                                        // Allow empty or valid decimal with up to 2 places and max $10,000
                                                                                        if (
                                                                                            value ===
                                                                                                "" ||
                                                                                            (/^\d*\.?\d{0,2}$/.test(
                                                                                                value,
                                                                                            ) &&
                                                                                                parseFloat(
                                                                                                    value ||
                                                                                                        "0",
                                                                                                ) <=
                                                                                                    10000)
                                                                                        ) {
                                                                                            setDonationInputs(
                                                                                                (
                                                                                                    inputs,
                                                                                                ) => ({
                                                                                                    ...inputs,
                                                                                                    [donation.id]:
                                                                                                        value,
                                                                                                }),
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                    onBlur={(
                                                                                        e,
                                                                                    ) => {
                                                                                        let dollarValue =
                                                                                            parseFloat(
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            );

                                                                                        // Cap at $10,000
                                                                                        if (
                                                                                            dollarValue >
                                                                                            10000
                                                                                        ) {
                                                                                            dollarValue = 10000;
                                                                                        }

                                                                                        if (
                                                                                            !e
                                                                                                .target
                                                                                                .value ||
                                                                                            isNaN(
                                                                                                dollarValue,
                                                                                            ) ||
                                                                                            dollarValue ===
                                                                                                0
                                                                                        ) {
                                                                                            setDonations(
                                                                                                (
                                                                                                    donations,
                                                                                                ) => {
                                                                                                    const newDonations =
                                                                                                        {
                                                                                                            ...donations,
                                                                                                        };
                                                                                                    delete newDonations[
                                                                                                        donation
                                                                                                            .id
                                                                                                    ];
                                                                                                    return newDonations;
                                                                                                },
                                                                                            );
                                                                                            setDonationInputs(
                                                                                                (
                                                                                                    inputs,
                                                                                                ) => ({
                                                                                                    ...inputs,
                                                                                                    [donation.id]:
                                                                                                        "0.00",
                                                                                                }),
                                                                                            );
                                                                                        } else {
                                                                                            const centsValue =
                                                                                                Math.round(
                                                                                                    dollarValue *
                                                                                                        100,
                                                                                                );
                                                                                            setDonations(
                                                                                                (
                                                                                                    donations,
                                                                                                ) => ({
                                                                                                    ...donations,
                                                                                                    [donation.id]:
                                                                                                        centsValue,
                                                                                                }),
                                                                                            );
                                                                                            setDonationInputs(
                                                                                                (
                                                                                                    inputs,
                                                                                                ) => ({
                                                                                                    ...inputs,
                                                                                                    [donation.id]:
                                                                                                        dollarValue.toFixed(
                                                                                                            2,
                                                                                                        ),
                                                                                                }),
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                    onFocus={(
                                                                                        e,
                                                                                    ) => {
                                                                                        // Select all on focus for easier editing
                                                                                        e.target.select();
                                                                                    }}
                                                                                    InputProps={{
                                                                                        startAdornment:
                                                                                            (
                                                                                                <InputAdornment position="start">
                                                                                                    $
                                                                                                </InputAdornment>
                                                                                            ),
                                                                                    }}
                                                                                    inputProps={{
                                                                                        min: 0,
                                                                                        max: 10000,
                                                                                        step: "0.01",
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                </Box>
                                                            </Grid>
                                                        ),
                                                    )}
                                                </Grid>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        onClick={previousStep}
                                                        sx={{ minWidth: 120 }}
                                                    >
                                                        Back
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        type="submit"
                                                        sx={{ minWidth: 120 }}
                                                    >
                                                        Next
                                                    </Button>
                                                </Box>
                                            </form>
                                        </Box>
                                    )}
                                    {/* Payment Step */}
                                    {step === paymentStep && (
                                        <Box sx={{ my: 2 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{ mb: 3 }}
                                            >
                                                Payment
                                            </Typography>
                                            {(PaymentProcessingStore
                                                .clientConfiguration
                                                .gatewayId ||
                                                (!PaymentProcessingStore
                                                    .clientConfiguration
                                                    .gatewayId &&
                                                    AuthStore.boxoffice)) &&
                                                cartStore && (
                                                    <PaymentForm
                                                        cartStore={cartStore}
                                                        total={
                                                            total +
                                                            donationsTotal
                                                        }
                                                        order={order}
                                                        address={address}
                                                        ecommercePayment={
                                                            ecommercePayment
                                                        }
                                                        setEcommercePayment={
                                                            setEcommercePayment
                                                        }
                                                        offlinePayment={
                                                            offlinePayment
                                                        }
                                                        setOfflinePayment={
                                                            setOfflinePayment
                                                        }
                                                        onSquareSubmit={
                                                            onSquareSubmit
                                                        }
                                                        onStripeSubmit={
                                                            onStripeSubmit
                                                        }
                                                        onStripeSuccess={
                                                            onStripeSuccess
                                                        }
                                                        onStripeError={
                                                            onStripeError
                                                        }
                                                        onIframeSubmit={
                                                            onIframeSubmit
                                                        }
                                                        onBack={previousStep}
                                                        loading={
                                                            cartStore.loading ||
                                                            isPaymentProcessing
                                                        }
                                                    />
                                                )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Right Column - Cart Summary (Desktop only) */}
                        <Grid
                            item
                            lg={4}
                            sx={{ display: { xs: "none", lg: "block" } }}
                        >
                            {cartStore && (
                                <CartSummary
                                    cartStore={cartStore}
                                    donations={donations}
                                />
                            )}
                        </Grid>
                    </Grid>
                )}
            </Container>

            {/* Standing room disclaimer dialog */}
            <Dialog open={showStandingRoomDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Standing Room Notice</DialogTitle>
                <DialogContent>
                    <Typography>
                        Please acknowledge the following before proceeding:
                    </Typography>
                    <ul>
                        <li>
                            This is a standing room ticket. There is no seat
                            associated to it.
                        </li>
                        <li>Performance view may be limited or obstructed.</li>
                        <li>
                            This ticket is non-refundable & not exchangeable.
                        </li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setShowStandingRoomDialog(false)}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Special client confirmation dialog */}
            {eventList?.clientId === 102 && step === paymentStep && (
                <Dialog
                    open={showConfirmDialog}
                    onClose={() => setShowConfirmDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Confirm Order</DialogTitle>
                    <DialogContent>
                        <Typography>
                            By clicking &quot;Confirm Order&quot;, you agree
                            that the items listed below are correct.
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            {cartStore?.packageItems.map((item) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mt: 1,
                                    }}
                                >
                                    <Typography>
                                        {item.eventPackage.name} |{" "}
                                        {item.eventPackagePriceGroup.name} -{" "}
                                        {item.seat?.seatDetails}
                                    </Typography>
                                </Box>
                            ))}
                            {cartStore?.seatmapItems.map((item) => (
                                <Box
                                    key={item.ticketId}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mt: 1,
                                    }}
                                >
                                    <Typography>
                                        {item.eventSeatmap?.name} -{" "}
                                        {item.sectionSeat?.seatDetails}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={previousStep}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setShowConfirmDialog(false)}
                        >
                            Confirm Order
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <Dialog open={!!order} fullWidth maxWidth="sm">
                {/* <DialogTitle>Complete Transaction on Terminal</DialogTitle> */}
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minHeight: "200px",
                            justifyContent: "space-evenly",
                        }}
                    >
                        <Typography>
                            Your transaction has been sent to the Square
                            Terminal for processing.
                        </Typography>
                        <CircularProgress size={50} />
                        <Button
                            variant="contained"
                            color="error"
                            disabled={wantsToCancel}
                            onClick={() => {
                                setWantsToCancel(true);
                                SalesEndpoints.cancelSquareTerminalCheckout(
                                    order!.retrievalKey,
                                ).catch(() => {
                                    setWantsToCancel(false);
                                });
                            }}
                        >
                            Cancel Transaction
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </PageWrapper>
    );
});

export default CheckoutForm;
